import { useState, type ReactNode } from "react";
import { CheckCircle2 } from "lucide-react";

export type SectionStatus = "empty" | "partial" | "complete" | "error";

export interface NavSection {
  id: string;
  label: string;
  icon: ReactNode;
  status: SectionStatus;
}

interface Props {
  sections: NavSection[];
  activeId: string;
  onSectionClick: (id: string) => void;
}

const statusDot: Record<SectionStatus, { color: string; symbol: ReactNode }> = {
  complete: { color: "var(--color-success-accent)", symbol: <CheckCircle2 size={12} /> },
  error:    { color: "var(--color-error-accent)", symbol: "●" },
  partial:  { color: "var(--color-warning-accent)", symbol: "●" },
  empty:    { color: "var(--color-surface-300)", symbol: "○" },
};

export function SectionAnchorNav({ sections, activeId, onSectionClick }: Props) {
  return (
    <nav style={{ padding: "20px 0" }}>
      <p style={{ fontSize: 11, fontWeight: 700, color: "var(--color-surface-400)", padding: "0 16px", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        Sections
      </p>
      {sections.map(s => (
        <NavItem key={s.id} section={s} isActive={s.id === activeId} onClick={() => onSectionClick(s.id)} />
      ))}
    </nav>
  );
}

function NavItem({ section, isActive, onClick }: { section: NavSection; isActive: boolean; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);
  const dot = statusDot[section.status];

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        width: "100%", padding: "8px 16px",
        background: isActive ? "var(--color-brand-50)" : hovered ? "var(--color-surface-50)" : "transparent",
        border: "none",
        borderLeft: isActive ? "3px solid var(--color-brand-600)" : "3px solid transparent",
        borderRadius: 0, cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span style={{ fontSize: 14 }}>{section.icon}</span>
      <span style={{
        fontSize: 12, fontWeight: isActive ? 700 : 500,
        color: isActive ? "var(--color-brand-700)" : "var(--color-surface-700)",
        flex: 1,
      }}>
        {section.label}
      </span>
      <span style={{ fontSize: 11, color: dot.color, fontWeight: 700 }}>{dot.symbol}</span>
    </button>
  );
}
